import React, { useMemo } from 'react';
import { toast } from 'sonner';

import { Wizard, WizardStepDefinition } from '@/components/postgrid/wizard';
import type {
  CreatePostgridContact,
  ExtractedRecipientInfo,
} from '@/services/api/postgridService';

import { ContactAddressStep } from './ContactAddressStep';
import { ContactDetailsStep } from './ContactDetailsStep';
import { ContactIdentityStep } from './ContactIdentityStep';
import { ContactReviewStep } from './ContactReviewStep';

export interface ContactWizardResult {
  payload: CreatePostgridContact;
  /** If true, skip PostGrid address verification and preserve user's original address */
  skipVerification: boolean;
}

interface ContactWizardProps {
  onSuccess: (result: ContactWizardResult) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  // Prefill optional fields
  initialData?: Partial<CreatePostgridContact>;
  /** Extracted recipient info from uploaded document - used to pre-populate form */
  extractedRecipient?: ExtractedRecipientInfo | null;
}

const contactWizardSteps: WizardStepDefinition[] = [
  {
    id: 'identity',
    title: 'Identity',
    description: 'Name & company',
  },
  {
    id: 'details',
    title: 'Contact',
    description: 'Email & phone',
  },
  {
    id: 'address',
    title: 'Address',
    description: 'Mailing address',
  },
  {
    id: 'review',
    title: 'Review',
    description: 'Confirm & submit',
  },
];

export const ContactWizard: React.FC<ContactWizardProps> = ({
  onSuccess,
  onCancel,
  isLoading = false,
  initialData = {},
  extractedRecipient,
}) => {
  // Compute which fields were AI-extracted for showing indicators
  const aiExtractedFields = useMemo(() => {
    if (!extractedRecipient?.extracted_fields) return new Set<string>();
    return new Set(extractedRecipient.extracted_fields);
  }, [extractedRecipient]);

  // Merge extracted recipient info with initialData, giving priority to initialData
  const mergedInitialData = useMemo(() => {
    if (!extractedRecipient) return initialData;

    const extractedData: Partial<CreatePostgridContact> = {
      first_name: extractedRecipient.first_name || undefined,
      last_name: extractedRecipient.last_name || undefined,
      company_name: extractedRecipient.company_name || undefined,
      address_line1: extractedRecipient.address_line1 || undefined,
      address_line2: extractedRecipient.address_line2 || undefined,
      city: extractedRecipient.city || undefined,
      province_or_state: extractedRecipient.province_or_state || undefined,
      postal_or_zip: extractedRecipient.postal_or_zip || undefined,
      country: extractedRecipient.country || 'US',
    };

    // Filter out undefined values
    const filteredExtracted = Object.fromEntries(
      Object.entries(extractedData).filter(([, v]) => v !== undefined),
    );

    return { ...filteredExtracted, ...initialData };
  }, [extractedRecipient, initialData]);

  const handleComplete = async (wizardData: Record<string, unknown>) => {
    try {
      const payload: CreatePostgridContact = {
        first_name: (wizardData.first_name as string) || null,
        last_name: (wizardData.last_name as string) || null,
        company_name: (wizardData.company_name as string) || null,
        email: (wizardData.email as string) || null,
        phone_number: (wizardData.phone_number as string) || null,
        address_line1: wizardData.address_line1 as string,
        address_line2: (wizardData.address_line2 as string) || null,
        city: wizardData.city as string,
        province_or_state: wizardData.province_or_state as string,
        postal_or_zip: wizardData.postal_or_zip as string,
        country: wizardData.country as string,
        notes: (wizardData.notes as string) || null,
        is_verified: false,
      };

      // If user typed address manually (didn't use autocomplete), skip verification
      const skipVerification = wizardData.skip_verification === true;

      // Pass result to parent - they handle API call and success toast
      onSuccess({ payload, skipVerification });
    } catch (error) {
      console.error('Failed to build contact payload:', error);
      toast.error('Failed to create contact. Please try again.');
    }
  };

  return (
    <Wizard
      title="Create New Contact"
      description={
        extractedRecipient
          ? 'Review the extracted recipient information and make any corrections.'
          : 'Follow the steps to add a new mailing contact.'
      }
      steps={contactWizardSteps}
      onComplete={handleComplete}
      onCancel={onCancel}
      isLoading={isLoading}
      initialData={mergedInitialData}
    >
      <ContactIdentityStep aiExtractedFields={aiExtractedFields} />
      <ContactDetailsStep />
      <ContactAddressStep aiExtractedFields={aiExtractedFields} />
      <ContactReviewStep />
    </Wizard>
  );
};
